import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Leaf, Package, Truck, FlaskConical, Sprout, RefreshCw, Plus, CheckCircle2,
  AlertTriangle, Loader2, X, Settings, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function MetrcDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Cannabis Compliance');
  const [dashboard, setDashboard] = useState(null);
  const [view, setView] = useState('overview');
  const [plants, setPlants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrcConfig, setMetrcConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ state: '', api_key: '', license_number: '', vendor_key: '' });
  const [testResult, setTestResult] = useState(null);
  const [configStates, setConfigStates] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/api/metrc/dashboard');
      setDashboard(data);
      if (data.plants.total === 0) {
        await api.post('/api/metrc/seed-demo');
        const { data: d2 } = await api.get('/api/metrc/dashboard');
        setDashboard(d2);
      }
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadView = async (v) => {
    setView(v);
    try {
      if (v === 'plants') { const { data } = await api.get('/api/metrc/plants?limit=50'); setPlants(data.plants || []); }
      if (v === 'packages') { const { data } = await api.get('/api/metrc/packages?limit=50'); setPackages(data.packages || []); }
      if (v === 'transfers') { const { data } = await api.get('/api/metrc/transfers?limit=50'); setTransfers(data.transfers || []); }
      if (v === 'lab') { const { data } = await api.get('/api/metrc/lab-results?limit=50'); setLabResults(data.lab_results || []); }
      if (v === 'settings') {
        const { data } = await api.get('/api/metrc/config');
        setMetrcConfig(data.config);
        setConfigStates(data.available_states || []);
        if (data.config) setConfigForm({ state: data.config.state || '', api_key: '', license_number: data.config.license_number || '', vendor_key: '' });
      }
    } catch (e) { /* handled */ }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="metrc-dashboard">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <Leaf className="w-6 h-6 text-emerald-600" /> METRC / BioTrack Integration
              </h1>
              <p className="text-sm text-gray-500 mt-1">Seed-to-sale tracking, compliance, and lab results</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>

          {/* Sub-nav */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[{k:'overview',l:'Overview'},{k:'plants',l:'Plants'},{k:'packages',l:'Packages'},{k:'transfers',l:'Transfers'},{k:'lab',l:'Lab Results'},{k:'settings',l:'Settings'}].map(t => (
              <button key={t.k} onClick={() => loadView(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`metrc-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* Overview */}
          {view === 'overview' && dashboard && (
            <div className="space-y-4" data-testid="metrc-overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Sprout} label="Total Plants" value={dashboard.plants.total} sub={`${dashboard.plants.vegetative} veg / ${dashboard.plants.flowering} flower`} />
                <StatCard icon={Package} label="Active Packages" value={dashboard.packages.active} />
                <StatCard icon={Truck} label="Transfers" value={`${dashboard.transfers.pending} pending`} sub={`${dashboard.transfers.in_transit} in transit`} />
                <StatCard icon={FlaskConical} label="Lab Results" value={`${dashboard.lab_results.passed} passed`} sub={`${dashboard.lab_results.failed} failed / ${dashboard.lab_results.pending} pending`} color={dashboard.lab_results.failed > 0 ? 'text-red-600' : 'text-emerald-600'} />
              </div>
            </div>
          )}

          {/* Plants */}
          {view === 'plants' && (
            <div className="space-y-3" data-testid="metrc-plants">
              {plants.map(p => (
                <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid={`metrc-plant-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${p.phase === 'flowering' ? 'bg-purple-500' : p.phase === 'harvest_ready' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                    <div>
                      <p className="text-sm font-bold text-gray-900">{p.strain}</p>
                      <p className="text-[10px] text-gray-400 font-mono">{p.tag} | {p.facility_name} | {p.room}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${p.phase === 'flowering' ? 'bg-purple-100 text-purple-700' : p.phase === 'harvest_ready' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{p.phase}</span>
                    <span className="text-[10px] text-gray-400">Planted: {p.planted_date}</span>
                  </div>
                </div>
              ))}
              {plants.length === 0 && <p className="text-center text-gray-400 py-8">No plants tracked</p>}
            </div>
          )}

          {/* Packages */}
          {view === 'packages' && (
            <div className="space-y-3" data-testid="metrc-packages">
              {packages.map(p => (
                <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid={`metrc-pkg-${p.id}`}>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{p.product_name}</p>
                    <p className="text-[10px] text-gray-400 font-mono">{p.tag} | {p.product_category} | {p.quantity} {p.unit}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.lab_tested ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                    <span className="text-[10px] text-gray-400">{p.lab_tested ? 'Lab Tested' : 'Untested'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Transfers */}
          {view === 'transfers' && (
            <div className="space-y-3" data-testid="metrc-transfers">
              {transfers.map(t => (
                <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`metrc-transfer-${t.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-gray-900 font-mono">{t.manifest_number}</p>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : t.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{t.status}</span>
                  </div>
                  <p className="text-xs text-gray-500">{t.from_facility} → {t.to_facility}</p>
                  <p className="text-[10px] text-gray-400 mt-1">Transporter: {t.transporter} | {t.package_count} packages</p>
                </div>
              ))}
            </div>
          )}

          {/* Lab Results */}
          {view === 'lab' && (
            <div className="space-y-3" data-testid="metrc-lab-results">
              {labResults.map(r => (
                <div key={r.id} className={`bg-white border rounded-lg p-4 ${r.passed === false ? 'border-red-200' : 'border-gray-200'}`} data-testid={`metrc-lab-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-gray-900">{r.lab_name} — {r.test_type}</p>
                    {r.passed === true && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">PASSED</span>}
                    {r.passed === false && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">FAILED</span>}
                    {r.passed === null && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">PENDING</span>}
                  </div>
                  <div className="flex gap-4 text-xs text-gray-600">
                    <span>THC: <strong>{r.thc_pct}%</strong></span>
                    <span>CBD: <strong>{r.cbd_pct}%</strong></span>
                    {r.tested_date && <span className="text-gray-400">Tested: {r.tested_date}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Settings */}
          {view === 'settings' && (
            <div className="space-y-4 max-w-2xl" data-testid="metrc-settings">
              {/* Connection Status */}
              <div className={`border rounded-lg p-5 flex items-center gap-3 ${metrcConfig?.api_key ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="metrc-connection-status">
                {metrcConfig?.api_key ? <Wifi className="w-5 h-5 text-emerald-600" /> : <WifiOff className="w-5 h-5 text-amber-600" />}
                <div>
                  <p className="text-sm font-bold text-gray-900">{metrcConfig?.api_key ? 'Live Mode' : 'Simulated Mode'}</p>
                  <p className="text-[10px] text-gray-500">{metrcConfig?.state_name ? `Connected to ${metrcConfig.state_name}` : 'Using demo data. Configure API key for live METRC integration.'}</p>
                </div>
              </div>

              {/* Config Form */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2"><Settings className="w-4 h-4 text-[#D4A843]" /> METRC API Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700">State *</label>
                    <select value={configForm.state} onChange={e => setConfigForm({...configForm, state: e.target.value})} className="w-full mt-1 h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="metrc-cfg-state">
                      <option value="">Select state...</option>
                      {configStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">License Number</label>
                    <Input value={configForm.license_number} onChange={e => setConfigForm({...configForm, license_number: e.target.value})} className="mt-1 rounded-lg text-xs font-mono" placeholder="Your METRC license #" data-testid="metrc-cfg-license" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">API Key *</label>
                    <Input type="password" value={configForm.api_key} onChange={e => setConfigForm({...configForm, api_key: e.target.value})} className="mt-1 rounded-lg text-xs font-mono" placeholder="Paste your METRC API key" data-testid="metrc-cfg-apikey" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Vendor API Key</label>
                    <Input type="password" value={configForm.vendor_key} onChange={e => setConfigForm({...configForm, vendor_key: e.target.value})} className="mt-1 rounded-lg text-xs font-mono" placeholder="Vendor-specific key (optional)" data-testid="metrc-cfg-vendor" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={async () => {
                    try {
                      await api.put('/api/metrc/config', configForm);
                      toast.success('METRC configuration saved');
                      loadView('settings');
                    } catch { toast.error('Save failed'); }
                  }} data-testid="metrc-cfg-save">Save Configuration</Button>
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={async () => {
                    try {
                      const { data } = await api.post('/api/metrc/config/test');
                      setTestResult(data);
                      toast[data.connected ? 'success' : 'warning'](data.connected ? 'Connection successful!' : `Connection failed: ${data.error}`);
                    } catch { toast.error('Test failed'); }
                  }} data-testid="metrc-cfg-test">Test Connection</Button>
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`border rounded-lg p-4 ${testResult.connected ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="metrc-test-result">
                  <p className="text-sm font-bold">{testResult.connected ? 'Connection Successful' : 'Connection Failed'}</p>
                  {testResult.error && <p className="text-xs text-red-600 mt-1">{testResult.error}</p>}
                  {testResult.facilities && <p className="text-xs text-emerald-600 mt-1">{testResult.facilities.length} facilities found</p>}
                </div>
              )}

              {/* State Info */}
              {configForm.state && configStates.find(s => s.id === configForm.state) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-bold text-gray-900">State: {configStates.find(s => s.id === configForm.state)?.name}</p>
                  <p className="text-[10px] text-gray-500 mt-1">API Endpoint: {configStates.find(s => s.id === configForm.state)?.api_base}</p>
                  <p className="text-[10px] text-gray-500">Regulatory Body: {configStates.find(s => s.id === configForm.state)?.regulatory_body}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
      <Icon className="w-5 h-5 text-[#D4A843] mb-2" />
      <p className={`font-heading text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}
